/**
 * Scan Agent Layout
 * 
 * Dedicated layout for scanning agents. Simpler than admin layout,
 * focused on the scanning workflow.
 * 
 * Access: scanning_agent OR admin roles only
 */

import React from 'react';
import { Navigate, Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import {
  BookOpen,
  Plus,
  LogOut,
  Loader2,
  ClipboardList,
  LayoutDashboard,
  Library,
  Store,
  MapPin,
} from 'lucide-react';
import { glassStyles } from '@/lib/design';

export function ScanAgentLayout() {
  const { user, profile, loading, profileLoaded, isAdmin, isScanningAgent, signOut } = useAuth();
  const location = useLocation();

  // Show loading while auth is initializing
  if (loading || !profileLoaded) {
    return (
      <div className="min-h-screen bg-[#FAF6F0] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#5C2D91] animate-spin" />
      </div>
    );
  }

  // Redirect if not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Redirect if not authorized (must be scanning_agent or admin)
  if (!isScanningAgent() && !isAdmin()) {
    return <Navigate to="/" replace />;
  }

  // Navigation items
  // - Content Pipeline: main dashboard (requests + in-progress books)
  // - Add New Book: proactive scanning (not tied to parent requests)
  const navItems = [
    { path: '/scan-agent', label: 'Content Pipeline', icon: ClipboardList, exact: true },
    { path: '/scan-agent/library', label: 'Global Library', icon: Library, exact: true },
    { path: '/scan-agent/new', label: 'Add New Book', icon: Plus, exact: true },
    { path: '/scan-agent/bookstore', label: 'Bookstore Books', icon: Store, exact: true },
    { path: '/scan-agent/library-lookup', label: 'Library Lookup', icon: MapPin, exact: true },
  ];

  const isActive = (path: string, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-[#FAF6F0]">
      {/* Top Navigation */}
      <header className="sticky top-0 z-40 border-b border-black/[0.08]" style={glassStyles.card}>
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          {/* Logo / Title */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#5E5CE6]/15 rounded-xl flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-[#5E5CE6]" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-[#1C1C1E]">Scan Agent</h1>
              <p className="text-xs text-[#8E8E93]">Book Processing Portal</p>
            </div>
          </div>

          {/* Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  isActive(item.path, item.exact)
                    ? 'bg-black/[0.05] text-[#1C1C1E]'
                    : 'text-[#636366] hover:text-[#1C1C1E] hover:bg-black/[0.03]'
                }`}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </Link>
            ))}
          </nav>

          {/* User Menu */}
          <div className="flex items-center gap-4">
            {/* Admin Portal Link - only for admins */}
            {isAdmin() && (
              <Link
                to="/admin"
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-[#636366] hover:text-[#1C1C1E] hover:bg-black/[0.03] transition-all"
                title="Switch to Admin Portal"
              >
                <LayoutDashboard className="w-4 h-4" />
                <span className="hidden sm:inline">Admin Portal</span>
              </Link>
            )}
            <div className="text-right hidden sm:block">
              <div className="text-sm font-medium text-[#1C1C1E]">{profile?.name || 'Agent'}</div>
              <div className="text-xs text-[#8E8E93]">
                {isAdmin() ? 'Admin' : 'Scanning Agent'}
              </div>
            </div>
            <button
              onClick={() => signOut()}
              className="p-2 rounded-lg hover:bg-black/[0.05] transition-colors text-[#636366] hover:text-[#1C1C1E]"
              title="Sign Out"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Navigation */}
      <nav className="md:hidden sticky top-16 z-30 border-b border-black/[0.08] bg-[#FAF6F0]/95 backdrop-blur-xl">
        <div className="flex overflow-x-auto px-4 py-2 gap-2">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                isActive(item.path, item.exact)
                  ? 'bg-black/[0.05] text-[#1C1C1E]'
                  : 'text-[#636366]'
              }`}
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </Link>
          ))}
          {/* Admin Portal Link - mobile */}
          {isAdmin() && (
            <Link
              to="/admin"
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap text-[#5E5CE6] hover:bg-[#5E5CE6]/10 transition-all"
            >
              <LayoutDashboard className="w-4 h-4" />
              Admin Portal
            </Link>
          )}
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        <Outlet />
      </main>
    </div>
  );
}

export default ScanAgentLayout;
